import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowUpRight,
  BarChart3,
  ChevronDown,
  Compass,
  SearchCheck,
  ShieldCheck,
} from "lucide-react";
import { lazy, Suspense } from "react";
import { Asterisk } from "@/components/Asterisk";
import { HeadingAccent } from "@/components/HeadingAccent";
import { ClientTestimonial } from "@/components/sections/ClientTestimonial";
import HeroHome640Avif from "@/assets/hero-home-640.avif";
import HeroHome1024Avif from "@/assets/hero-home-1024.avif";
import HeroHome640Webp from "@/assets/hero-home-640.webp";
import HeroHome1024Webp from "@/assets/hero-home-1024.webp";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const RelatedNews = lazy(() =>
  import("@/components/sections/RelatedNews").then((module) => ({
    default: module.RelatedNews,
  })),
);

const faqs = [
  {
    q: "Do you work with businesses outside Barnsley?",
    a: "Yes. While Barnsley is a big part of my work, I also support businesses across Yorkshire and further afield, so the best place to start is usually my areas covered page.",
  },
  {
    q: "What SEO services do you offer?",
    a: "I provide practical SEO services built around what will actually help the business, whether that means technical fixes, local SEO, better service pages, or a clearer long-term strategy.",
  },
  {
    q: "Do you work with small businesses?",
    a: "Yes. A lot of my clients are smaller businesses that need straightforward support rather than layers of process, which is why my services overview is built around practical work rather than bloated retainers.",
  },
  {
    q: "Can you help with an existing website?",
    a: "Yes. In many cases the smartest move is improving what is already there, especially if the site would benefit from stronger structure, clearer calls to action, or ongoing website management.",
  },
  {
    q: "Can you help improve website quote requests?",
    a: "Yes. More traffic only gets you so far, so I often look at how the site guides people towards contact, trust, and conversion alongside the main web design work.",
  },
];

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "SEO Barnsley | Consultant-Led SEO & Web Design Support" },
      {
        name: "description",
        content:
          "Work directly with a Barnsley SEO consultant on audits, local SEO and web design that improves enquiry quality. Book a practical review with Phil today.",
      },
      {
        property: "og:title",
        content: "SEO Barnsley | Consultant-Led SEO & Web Design Support",
      },
      {
        property: "og:description",
        content:
          "SEO, web design, and digital strategy services to help UK businesses grow online.",
      },
      { property: "og:url", content: "https://www.phil-carr.co.uk/" },
      {
        property: "og:image",
        content: "https://www.phil-carr.co.uk/images/hero-home-generated.jpg",
      },
      {
        name: "twitter:title",
        content: "SEO Barnsley | Consultant-Led SEO & Web Design Support",
      },
      {
        name: "twitter:description",
        content:
          "SEO, web design, and digital strategy services to help UK businesses grow online.",
      },
      {
        name: "twitter:image",
        content: "https://www.phil-carr.co.uk/images/hero-home-generated.jpg",
      },
    ],
    links: [{ rel: "canonical", href: "https://www.phil-carr.co.uk/" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Home,
});

const services = [
  { label: "Technical Audit", to: "/seo/bespoke-seo-website-audits" },
  { label: "Keyword Research", to: "/seo/organic-seo-services" },
  { label: "Competitor Analysis", to: "/seo/bespoke-seo-website-audits" },
  { label: "Content Strategy", to: "/seo/organic-seo-services" },
  { label: "On-Page Optimisation", to: "/seo/organic-seo-services" },
  { label: "Technical SEO", to: "/seo/technical-seo-support" },
  { label: "Mobile Optimisation", to: "/web-design" },
  { label: "E-Commerce SEO", to: "/shopify-websites" },
  { label: "Local SEO & GMB Management", to: "/seo/organic-seo-services" },
  { label: "Link Building", to: "/seo/link-building-outreach" },
  { label: "Conversion Optimisation", to: "/web-design" },
  { label: "Analytics Tracking", to: "/seo/bespoke-seo-website-audits" },
  { label: "Reporting & Insights", to: "/seo/bespoke-seo-website-audits" },
  { label: "Based in Barnsley, South Yorkshire", to: "/areas/barnsley" },
  { label: "Custom CMS — WordPress, Moodle, Joomla", to: "/wordpress-websites" },
  { label: "Web Design Consultant", to: "/web-design" },
] as const;

const steps = [
  {
    n: "01",
    icon: SearchCheck,
    title: "Audit and diagnosis",
    body: "Identify the technical, content, and structure issues that are holding back growth.",
  },
  {
    n: "02",
    icon: Compass,
    title: "Priority roadmap",
    body: "Set out what needs doing first, what can wait, and what should drive the biggest return.",
  },
  {
    n: "03",
    icon: ShieldCheck,
    title: "Strategy implementation",
    body: "Fix technical issues, upgrade key pages, and strengthen local and service-intent relevance.",
  },
  {
    n: "04",
    icon: BarChart3,
    title: "Review and refinement",
    body: "Track rankings, traffic quality, and enquiries, then refine monthly priorities based on data.",
  },
];

function Home() {
  return (
    <div>
      {/* Homepage hero */}
      <section className="home-hero relative overflow-hidden">
        <div className="container relative mx-auto max-w-7xl px-6 pt-28 pb-20 lg:px-10 lg:pt-44 lg:pb-28">
          <div className="row g-5 align-items-center">
            <div className="col-lg-7">
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary/60 text-xs uppercase tracking-widest text-muted-foreground backdrop-blur font-bold mx-[5px] my-[15px] py-[8px] px-[12px]">
                <Asterisk className="h-3 w-3 text-primary" />
                SEO &amp; Web design — Yorkshire, UK
              </div>
              <h1 className="site-heading-lg display-xl mt-8 max-w-5xl text-foreground">
                SEO Barnsley &amp; Web Design <span className="accent-word">Services</span> That
                Bring in{" "}
                <span className="accent-word">
                  <HeadingAccent>Better Enquiries</HeadingAccent>
                </span>
              </h1>
              <p className="site-copy mt-8 max-w-3xl text-muted-foreground">
                Most businesses do not need more traffic for the sake of it. They need the right
                people landing on the right pages, then getting in touch. I combine technical SEO
                expertise with conversion-focused web design so your website does more than look
                polished — it helps turn visits into enquiries.
              </p>
              <dl className="mt-8 grid max-w-3xl gap-4 sm:grid-cols-3">
                {[
                  { value: "12+", label: "years SEO experience" },
                  { value: "SEO", label: "and web design" },
                  { value: "Direct", label: "consultant-led delivery" },
                ].map((item) => (
                  <div key={item.label} className="border border-border bg-background/70 p-4">
                    <dt className="font-display text-2xl text-foreground">{item.value}</dt>
                    <dd className="mt-1 text-xs uppercase tracking-wider text-muted-foreground">
                      {item.label}
                    </dd>
                  </div>
                ))}
              </dl>
              <div className="mt-10 flex flex-wrap items-center gap-8">
                <Link
                  to="/contact"
                  className="group demo-button-primary demo-arrow-btn inline-flex items-center gap-3 rounded-full py-0 pl-7 pr-0 text-sm font-bold transition-transform hover:-translate-y-0.5"
                >
                  Book A Consultation
                  <span className="demo-cta-arrow-icon grid h-12 w-12 place-items-center rounded-full text-primary-foreground transition-transform group-hover:rotate-45">
                    <ArrowUpRight className="h-5 w-5" />
                  </span>
                </Link>
                <Link
                  to="/services"
                  className="group demo-button-primary inline-flex items-center gap-3 px-7 py-4 text-sm font-bold transition-transform hover:-translate-y-0.5"
                >
                  Explore services
                  <span className="demo-cta-arrow-icon grid h-12 w-12 place-items-center rounded-full text-primary-foreground">
                    <ArrowUpRight className="h-5 w-5 transition-transform group-hover:rotate-45" />
                  </span>
                </Link>
              </div>
            </div>
            <div className="col-lg-5 mt-10 lg:mt-0">
              <div className="relative aspect-[16/10] overflow-hidden rounded-[10px] border border-primary/30 shadow-[0_30px_80px_-40px_rgba(179,255,10,0.35)]">
                <picture>
                  <source
                    type="image/avif"
                    srcSet={`${HeroHome640Avif} 640w, ${HeroHome1024Avif} 1024w`}
                    sizes="(min-width: 1024px) 40vw, 100vw"
                  />
                  <source
                    type="image/webp"
                    srcSet={`${HeroHome640Webp} 640w, ${HeroHome1024Webp} 1024w`}
                    sizes="(min-width: 1024px) 40vw, 100vw"
                  />
                  <img
                    src={HeroHome640Webp}
                    srcSet={`${HeroHome640Webp} 640w, ${HeroHome1024Webp} 1024w`}
                    sizes="(min-width: 1024px) 40vw, 100vw"
                    width={1024}
                    height={640}
                    alt="SEO and web design dashboards showing keyword rankings, conversion funnels and Yorkshire local map data"
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                </picture>
              </div>
            </div>
          </div>
        </div>

        <a
          href="#home-intro"
          className="home-hero-scroll-cue"
          aria-label="Scroll to practical SEO and web support section"
        >
          <ChevronDown className="h-7 w-7" aria-hidden="true" />
        </a>

        {/* MARQUEE — lime band */}
        <div className="relative border-y border-primary bg-primary py-3 text-primary-foreground">
          <div className="marquee">
            {[0, 1].map((track) => (
              <div
                className="marquee-track"
                aria-hidden={track === 1 ? true : undefined}
                key={track}
              >
                {services.map((s) =>
                  track === 0 ? (
                    <Link
                      to={s.to}
                      key={s.label}
                      preload="intent"
                      className="flex items-center gap-4 whitespace-nowrap text-sm font-semibold transition-colors hover:text-primary focus-visible:text-primary focus-visible:outline-none"
                    >
                      <Asterisk className="h-4 w-4" />
                      {s.label}
                    </Link>
                  ) : (
                    <span
                      key={s.label}
                      className="flex items-center gap-4 whitespace-nowrap text-sm font-semibold"
                    >
                      <Asterisk className="h-4 w-4" />
                      {s.label}
                    </span>
                  ),
                )}
                {services.map((s) => (
                  <span
                    key={`${s.label}-repeat`}
                    aria-hidden="true"
                    className="flex items-center gap-4 whitespace-nowrap text-sm font-semibold"
                  >
                    <Asterisk className="h-4 w-4" />
                    {s.label}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INTRO / DRIVING GROWTH */}
      <section
        id="home-intro"
        className="container mx-auto max-w-7xl scroll-mt-24 px-6 pb-24 pt-14 lg:px-10 lg:pb-32 lg:pt-20"
      >
        <div className="row g-5">
          <div className="col-lg-5">
            <p className="demo-section-label">Practical SEO and web support for UK businesses</p>
            <h2 className="site-heading-md mt-4 font-display font-light">
              Driving steady growth through smarter <em>SEO</em>
            </h2>
          </div>
          <div className="col-lg-7">
            <p className="site-copy text-muted-foreground">
              I provide SEO audits, keyword research, technical fixes, and web design for businesses
              that want better enquiry quality. In most cases, the biggest gains come from sorting
              the fundamentals first, then building the right pages around real search demand.
            </p>
            <ul className="bootstrap-list-row mt-8 row -translate-x-[30px] list-none pl-0">
              {[
                "Clear technical audits with practical priorities",
                "Keyword strategy based on commercial intent",
                "On-page and technical SEO improvements",
                "Local SEO & Google Business Profile management",
              ].map((item) => (
                <li key={item} className="col-sm-6 flex items-start gap-3 pl-0">
                  <Asterisk className="mt-1 h-4 w-4 shrink-0 text-accent" />
                  <span className="text-foreground">{item}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/about"
              className="mt-10 inline-flex items-center gap-3 demo-button-primary px-7 py-4 text-sm font-semibold transition-transform hover:-translate-y-0.5"
            >
              Learn more about how I work
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="border-y border-border bg-card/80">
        <div className="container mx-auto max-w-7xl px-6 py-24 lg:px-10">
          <div className="row g-5 align-items-start">
            <div className="col-lg-5 lg:sticky lg:top-28">
              <p className="demo-section-label">What you can expect</p>
              <h2 className="site-heading-md mt-4 max-w-lg font-display font-light">
                Practical support with no <em>guesswork</em>
              </h2>
              <div className="mt-8 row row-cols-2 g-4 max-w-lg" aria-hidden="true">
                {[SearchCheck, Compass, ShieldCheck, BarChart3].map((Icon, index) => (
                  <div key={index} className="col">
                    <div className="flex aspect-square items-center justify-center bg-background/70 text-primary">
                      <Icon className="h-12 w-12" strokeWidth={1.5} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="col-lg-7 d-grid gap-4">
              {[
                {
                  title: "Work you can track",
                  body: "You can see the work completed and the reason it was prioritised. I review search performance and enquiry data with you, explain what the evidence supports and keep the next actions clear.",
                },
                {
                  title: "Strategy matched to your market",
                  body: "I assess your services, competitors and existing website before recommending a plan. The priorities reflect the customers you want to reach and the time and resources available to deliver the work.",
                },
                {
                  title: "Straight answers",
                  body: "You deal directly with me and receive advice in plain English. I explain the scope, likely dependencies and any uncertainty before we commit to work, so you can make an informed decision about your website.",
                },
              ].map((p, idx) => (
                <div
                  key={p.title}
                  className="group row g-4 border border-border bg-background/70 p-6 transition-colors hover:border-primary sm:p-8"
                >
                  <div className="col-auto">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full border border-border bg-secondary/70 text-lg font-semibold text-primary transition-colors group-hover:border-primary">
                      0{idx + 1}
                    </div>
                  </div>
                  <div className="col">
                    <h3 className="site-heading-md font-display">{p.title}</h3>
                    <p className="site-copy mt-3 max-w-2xl text-muted-foreground">{p.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="container mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
        <div className="row">
          <div className="col-lg-8">
            <p className="demo-section-label">How the work actually runs</p>
            <h2 className="site-heading-md mt-4 font-display font-light">
              A clear four-step process from <em>audit</em> to <em>growth</em>
            </h2>
            <p className="site-copy mt-6 text-muted-foreground">
              A lot of businesses struggle because SEO feels vague. This keeps it practical: find
              the issues, fix the priorities, improve the key pages, then measure what actually
              drives enquiries.
            </p>
          </div>
        </div>
        <div className="demo-process-grid mt-14 row g-4">
          {steps.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.n} className="col-md-6 col-lg-3">
                <div className="demo-process-card d-flex flex-column gap-4 h-100 border border-border bg-background px-7 py-8 transition-colors hover:bg-card sm:px-8 sm:py-10">
                  <div className="flex items-center justify-between">
                    <Icon className="h-10 w-10 text-accent" strokeWidth={1.5} />
                    <span className="font-display text-2xl leading-none text-muted-foreground">
                      {s.n}
                    </span>
                  </div>
                  <h3 className="self-start font-display text-2xl leading-tight">{s.title}</h3>
                  <div className="pt-4">
                    <p className="text-sm leading-relaxed text-muted-foreground">{s.body}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <ClientTestimonial />

      {/* FAQ */}
      <section className="demo-faq-section border-y border-border">
        <div className="container mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <div className="row">
            <div className="col-lg-12">
              <div className="demo-section-heading text-left mb-12">
                <h3 className="demo-section-label">SEO FAQs</h3>
                <h2 className="site-heading-md mt-4 font-display font-light">
                  Common <span className="font-normal italic">SEO</span> questions
                </h2>
              </div>
              <Accordion type="single" collapsible className="demo-accordion w-full">
                {faqs.map((f, i) => (
                  <AccordionItem key={f.q} value={`faq-${i}`} className="demo-accordion-item">
                    <AccordionTrigger className="demo-accordion-trigger group relative flex w-full text-left font-display hover:no-underline [&>svg.lucide-chevron-down]:hidden">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="demo-accordion-content text-base leading-relaxed">
                      <p>{f.a}</p>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <Suspense fallback={null}>
        <RelatedNews count={10} title="Latest news" eyebrow="From the blog" />
      </Suspense>

      {/* CTA */}
      <section className="container mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
        <div className="relative overflow-hidden rounded-[10px] border border-primary/40 bg-background p-10 text-foreground shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] lg:p-16">
          <img
            src="/images/about-bg-star.svg"
            alt="Decorative star graphic for SEO enquiry call to action"
            data-name="Decorative star graphic for SEO enquiry call to action"
            title="Decorative star graphic for SEO enquiry call to action"
            aria-hidden="true"
            loading="lazy"
            decoding="async"
            className="img-fluid pointer-events-none absolute -right-20 -top-20 h-48 w-48 max-w-[55vw] object-contain opacity-20 spin-slow sm:h-64 sm:w-64 lg:h-80 lg:w-80"
          />
          <div className="relative max-w-2xl">
            <h2 className="site-heading-md font-display font-light">
              Want better enquiries from your website?
            </h2>
            <p className="site-copy mt-5 text-muted-foreground">
              Tell me about your business and your goals. I'll come back with a clear view on what's
              worth doing first.
            </p>
            <Link
              to="/contact"
              className="mt-8 inline-flex items-center gap-3 demo-button-primary rounded-full px-7 py-4 text-sm font-semibold transition-transform hover:-translate-y-0.5"
            >
              Start a conversation
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
